import { Grid, IconButton } from "@mui/material";
import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import { useDispatch, useSelector } from "react-redux";
import AreaFieldItemLoader from "../../../viewer/common/fields/area-fields/AreaFieldItemLoader.tsx";
import FieldItemCaptions from "../../../viewer/common/fields/area-fields/FieldItemCaptions.tsx";
import FieldItemOptionsButton from "../../../viewer/common/fields/area-fields/FieldItemOptionsButton.tsx";
import type { FieldItemProps } from "../../../viewer/common/fields/area-fields/areaFiledItem.types.ts";
import { selectAutoShowItemOptionsId } from "../../../../store/selectors/currentReportSelectors";
import { createCurrentReportAction } from "../../../../store/utils/storeUtils";

interface FilterFieldItemProps<T> extends FieldItemProps<T> {
  startIcon?: JSX.Element;
  expandable?: boolean;
  onToggle?: (expanded: boolean) => void;
  disableExpanding?: boolean;
  copyButton?: JSX.Element | null;
}

export const FilterFieldItem = <T,>({
  field,
  hideOptions,
  formatCaption,
  onShowOptions,
  optionRef,
  isLoading,
  startIcon,
  expandable,
  onToggle,
  disableExpanding,
  copyButton,
}: FilterFieldItemProps<T>) => {
  const autoShowItemOptionsId = useSelector(selectAutoShowItemOptionsId);
  const dispatch = useDispatch();

  const [optionElementRef, setOptionElementRef] = useState<HTMLElement | null>(null);
  const [conditionExpanded, setConditionExpanded] = useState(false);

  useEffect(() => {
    optionRef?.call(null, optionElementRef);
  }, [optionRef, optionElementRef]);

  const showOptions = useCallback(() => {
    if (!onShowOptions) return false;
    const ref = optionElementRef;
    if (!ref) return false;

    onShowOptions({ field: field, ref, showOnlySelected: false });
    return true;
  }, [optionElementRef, field, onShowOptions]);

  useEffect(() => {
    if (!autoShowItemOptionsId) return;
    const item = field as { config: { guid: string; filter: { guid: string } }; guid: string };
    if (
      autoShowItemOptionsId === item?.guid ||
      item.config?.guid === autoShowItemOptionsId ||
      item.config?.filter?.guid === autoShowItemOptionsId
    ) {
      if (showOptions()) {
        dispatch(createCurrentReportAction("setAutoShowItemOptionsId", undefined));
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [autoShowItemOptionsId, field, showOptions]);

  return (
    <>
      {isLoading === true && <AreaFieldItemLoader />}
      <Grid container sx={{ justifyContent: "space-between", width: "100%" }}>
        {expandable && (
          <Grid
            container
            sx={(theme) => ({
              alignItems: "center",
              px: "5px",
              height: "30px",
              width: "auto",
              "& .MuiSvgIcon-root": {
                color: startIcon ? theme.palette.secondary.light : "inherit",
              },
            })}
          >
            <IconButton
              disabled={disableExpanding}
              onClick={() => {
                setConditionExpanded((prev) => {
                  onToggle?.(!prev);
                  return !prev;
                });
              }}
              sx={{ "& .MuiSvgIcon-root": disableExpanding ? { color: "action.disabled" } : {} }}
            >
              {conditionExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
            {startIcon && startIcon}
          </Grid>
        )}
        <Grid
          container
          sx={{
            alignItems: "center",
            px: "5px",
            height: "28px",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            mt: "auto",
            width: "100%",
          }}
        >
          <FieldItemCaptions field={field} formatCaption={formatCaption} />
        </Grid>
        {copyButton && copyButton}
        <FieldItemOptionsButton hideOptions={hideOptions} showOptions={showOptions} ref={setOptionElementRef} />
      </Grid>
    </>
  );
};
